/**
 * A writing-centered workspace with specialist views and a persistent AI companion.
 * The container owns document/preview data; this shell owns only local panel visibility.
 * Legacy callers without a writingSlot retain their existing view navigation.
 */

import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { BookPlus, ChevronDown, Download, LogOut, Plus, WalletCards, PanelLeft, PanelRightClose, Play, MessageSquare, PenLine } from "lucide-react";
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipProvider,
} from "../../ui/index.js";
import { LanguageToggle } from "../../ui/LanguageToggle.js";
import { useTr, type Entry } from "../../i18n/context.js";
import { COMMON } from "../../i18n/common.js";
import "./WorkspaceShell.css";

/** Shared workspace modes; preview remains addressable for existing deep links. */
export type WorkspaceView = "writing" | "studio" | "editor" | "canvas" | "assets" | "preview";

/** 剧集导航条目（容器只需提供 id，标题即 id）。 */
export interface WorkspaceEpisodeRef {
  readonly id: string;
}

/** 书籍（project）条目，用于顶栏的书籍切换。 */
export interface WorkspaceBookRef {
  readonly id: string;
  readonly name: string;
}

const S = {
  writing: { zh: "写故事", en: "Write" },
  structure: { zh: "故事地图", en: "Story map" },
  staging: { zh: "演出", en: "Stage" },
  script: { zh: "脚本", en: "Script" },
  preview: { zh: "预览", en: "Preview" },
  companion: { zh: "创作助手", en: "Writing companion" },
  hideCompanion: { zh: "收起侧栏", en: "Hide companion" },
  toggleOutline: { zh: "显示或收起目录", en: "Toggle outline" },
  location: { zh: "位置", en: "Location" },
  switchBook: { zh: "切换作品", en: "Switch book" },
  centralView: { zh: "中央视图", en: "View" },
  aiAssistant: { zh: "AI 助手", en: "AI assistant" },
  accountBalance: { zh: "账户余额", en: "Account balance" },
  accountBalanceLoading: { zh: "查询中", en: "Checking" },
  accountBalanceUnavailable: { zh: "暂不可用", en: "Unavailable" },
  unlimited: { zh: "无限", en: "Unlimited" },
  unlimitedBalance: { zh: "无限额度", en: "Unlimited" },
  usagePending: { zh: "用量待返回", en: "Usage pending" },
  usedThisMonth: { zh: "本月已用 {used} / {quota}", en: "Used this month {used} / {quota}" },
} satisfies Record<string, Entry>;

export interface WorkspaceAccountCostUsage {
  readonly period: string;
  readonly currency: "USD";
  readonly usedCostUsd: number;
  readonly quotaCostUsd: number | null;
  readonly remainingCostUsd?: number;
  readonly unlimited?: boolean;
}

export interface WorkspaceAccountTokenUsage {
  readonly period: string;
  readonly used: number;
  readonly quota: number | null;
  readonly remaining?: number;
  readonly unlimited?: boolean;
}

export type WorkspaceAccountUsage = WorkspaceAccountCostUsage | WorkspaceAccountTokenUsage;

function trimFixed(value: string): string {
  return value.replace(/\.?0+$/, "");
}

function formatUsd(value: number, trim = false): string {
  if (!Number.isFinite(value)) return "$0";
  const abs = Math.abs(value);
  if (abs !== 0 && abs < 0.01) return `$${trimFixed(value.toFixed(6))}`;
  const fixed = value.toFixed(2);
  return `$${trim ? trimFixed(fixed) : fixed}`;
}

export interface WorkspaceShellProps {
  /** 顶栏面包屑里的项目名（书名）。 */
  readonly projectName: string;
  /** 当前选中的剧集 id（驱动左侧高亮 + 面包屑尾段）。 */
  readonly episodeId?: string;
  /** 左侧导航的剧集列表。 */
  readonly episodes: readonly WorkspaceEpisodeRef[];
  /** 选中某个剧集。 */
  readonly onSelectEpisode: (id: string) => void;
  /** 可选「新建剧集」动作；提供时左侧导航尾部出现 + 入口。 */
  readonly onNewEpisode?: () => void;
  /** 全部书籍（project）列表；提供时书名可下拉切换。 */
  readonly books?: readonly WorkspaceBookRef[];
  /** 切换到另一本书。 */
  readonly onSwitchBook?: (id: string) => void;
  /** 新建一本书（回到起步引导）。 */
  readonly onNewBook?: () => void;
  /** 下载当前项目 zip 包。 */
  readonly onDownloadProject?: () => void;
  /** 下载按钮忙状态。 */
  readonly projectDownloadBusy?: boolean;
  /** 顶栏账号 chip 文案（登录名 / “访客”）。 */
  readonly accountLabel?: string;
  /** 当前账户余额/用量；来自网关 usage ledger，WorkspaceShell 只负责展示。 */
  readonly accountUsage?: WorkspaceAccountUsage | null;
  readonly accountUsageLoading?: boolean;
  readonly accountUsageError?: string | null;
  /** 退出登录。 */
  readonly onLogout?: () => void;
  /** Guest access to the authentication overlay. */
  readonly onSignIn?: () => void;
  /** 中央当前视图。 */
  readonly activeView: WorkspaceView;
  /** 切换中央视图。 */
  readonly onSelectView: (view: WorkspaceView) => void;
  /** 右侧 agent 侧栏是否收起（收起时 body 网格收窄）。 */
  readonly sidebarCollapsed?: boolean;
  /** Continuous authoring surface. Supplying it enables writing-first navigation. */
  readonly writingSlot?: ReactNode;
  readonly companionOpenKey?: number;
  readonly companionMode?: "agent" | "preview";
  readonly onCompanionModeChange?: (mode: "agent" | "preview") => void;
  readonly onPreview?: () => void;
  /** Existing specialist surfaces, supplied by the container. */
  readonly studioSlot?: ReactNode;
  readonly editorSlot?: ReactNode;
  readonly canvasSlot?: ReactNode;
  readonly assetsSlot?: ReactNode;
  readonly previewSlot?: ReactNode;
  /** 右侧常驻 agent 侧栏内容（对话第一入口）。 */
  readonly sidebarSlot?: ReactNode;
}

export function WorkspaceShell(props: WorkspaceShellProps): ReactElement {
  const {
    projectName,
    episodeId,
    episodes,
    onSelectEpisode,
    onNewEpisode,
    books,
    onSwitchBook,
    onNewBook,
    onDownloadProject,
    projectDownloadBusy,
    accountLabel,
    accountUsage,
    accountUsageLoading,
    accountUsageError,
    onLogout,
    onSignIn,
    activeView,
    onSelectView,
    sidebarCollapsed,
    writingSlot,
    companionMode,
    companionOpenKey,
    onCompanionModeChange,
    onPreview,
    studioSlot,
    editorSlot,
    canvasSlot,
    assetsSlot,
    previewSlot,
    sidebarSlot,
  } = props;
  const tr = useTr();

  const [outlineCollapsed, setOutlineCollapsed] = useState(false);
  const [companionHidden, setCompanionHidden] = useState(false);
  const [localCompanionMode, setLocalCompanionMode] = useState<"agent" | "preview">("preview");
  useEffect(() => { setCompanionHidden(false); }, [companionOpenKey]);
  const selectedCompanion = companionMode ?? localCompanionMode;
  const isWriting = activeView === "writing";
  const writingEnabled = writingSlot !== undefined || isWriting;
  const selectCompanion = (mode: "agent" | "preview") => {
    setCompanionHidden(false);
    setLocalCompanionMode(mode);
    onCompanionModeChange?.(mode);
  };
  const openPreview = () => {
    if (isWriting) selectCompanion("preview");
    else onSelectView("preview");
    onPreview?.();
  };
  const views: readonly { value: WorkspaceView; label: string }[] = writingEnabled ? [
    { value: "writing", label: tr(S.writing) },
    { value: "canvas", label: tr(S.structure) },
    { value: "studio", label: tr(S.staging) },
    { value: "assets", label: tr(COMMON.tabAssets) },
    { value: "editor", label: tr(S.script) },
  ] : [
    { value: "studio", label: tr(COMMON.tabStudio) },
    { value: "editor", label: tr(COMMON.tabEditor) },
    { value: "canvas", label: tr(COMMON.tabCanvas) },
    { value: "assets", label: tr(COMMON.tabAssets) },
    { value: "preview", label: tr(COMMON.tabPreview) },
  ];

  const canSwitchBooks = Boolean(onSwitchBook || onNewBook);
  const accountUsageContent = (() => {
    if (accountUsageLoading) {
      return { value: tr(S.accountBalanceLoading), detail: tr(S.accountBalance), tone: "loading" as const };
    }
    if (accountUsageError) {
      return { value: tr(S.accountBalanceUnavailable), detail: tr(S.accountBalance), tone: "error" as const };
    }
    if (!accountUsage) return null;
    if ("usedCostUsd" in accountUsage) {
      const unlimited = accountUsage.unlimited === true || accountUsage.quotaCostUsd === null;
      const quota = unlimited ? tr(S.unlimited) : formatUsd(accountUsage.quotaCostUsd, true);
      const used = formatUsd(accountUsage.usedCostUsd);
      const remaining =
        typeof accountUsage.remainingCostUsd === "number"
          ? Math.max(0, accountUsage.remainingCostUsd)
          : unlimited
            ? undefined
            : Math.max(0, accountUsage.quotaCostUsd - accountUsage.usedCostUsd);
      return {
        value: unlimited ? tr(S.unlimitedBalance) : formatUsd(remaining ?? 0),
        detail: tr(S.usedThisMonth, { used, quota }),
        tone: "ready" as const,
      };
    }
    return {
      value: accountUsage.unlimited ? tr(S.unlimitedBalance) : tr(S.usagePending),
      detail: tr(S.accountBalance),
      tone: "ready" as const,
    };
  })();

  return (
    <TooltipProvider>
      <div className="ws-shell" data-active-view={activeView}>
        <header className="ws-topbar">
          <div className="ws-brand">
            {writingEnabled && <IconButton label={tr(S.toggleOutline)} size="sm" aria-expanded={!outlineCollapsed} onClick={() => setOutlineCollapsed((value) => !value)}><PanelLeft size={17} /></IconButton>}
            <span className="ws-wordmark">{tr(COMMON.appName)}</span>
            <nav className="ws-crumb" aria-label={tr(S.location)}>
              {canSwitchBooks ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="ws-crumb-book" aria-label={tr(S.switchBook)}>
                      <span className="ws-crumb-project">{projectName}</span>
                      <ChevronDown size={14} aria-hidden="true" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuLabel>{tr(COMMON.episodes)}</DropdownMenuLabel>
                    {(books ?? []).map((b) => (
                      <DropdownMenuItem key={b.id} onSelect={() => onSwitchBook?.(b.id)}>
                        {b.name}
                      </DropdownMenuItem>
                    ))}
                    {onNewBook ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem icon={<BookPlus size={15} />} onSelect={() => onNewBook()}>
                          {tr(COMMON.newBook)}
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span className="ws-crumb-project">{projectName}</span>
              )}
              {episodeId ? (
                <>
                  <span className="ws-crumb-sep" aria-hidden="true">
                    /
                  </span>
                  <span className="ws-crumb-ep">{episodeId}</span>
                </>
              ) : null}
            </nav>
          </div>

          <div className="ws-topbar-actions">
            {writingEnabled && <>
              {isWriting && <Button variant="ghost" size="sm" leadingIcon={<MessageSquare size={15} />} onClick={() => selectCompanion("agent")}>AI</Button>}
              <Button className="ws-preview-action" size="sm" leadingIcon={<Play size={15} />} onClick={openPreview}>{tr(S.preview)}</Button>
            </>}
            {onDownloadProject ? (
              <Tooltip content={tr(COMMON.downloadProject)}>
                <Button
                  className="ws-download-btn"
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Download size={15} aria-hidden="true" />}
                  loading={projectDownloadBusy}
                  onClick={onDownloadProject}
                >
                  {tr(COMMON.download)}
                </Button>
              </Tooltip>
            ) : null}
            <LanguageToggle />
            {accountLabel || onSignIn ? (
              <div className="ws-account">
                {accountUsageContent ? (
                  <span
                    className="ws-account-balance"
                    data-tone={accountUsageContent.tone}
                    aria-label={`${tr(S.accountBalance)} ${accountUsageContent.value}`}
                    title={accountUsageContent.detail}
                  >
                    <WalletCards size={15} aria-hidden="true" />
                    <span className="ws-account-balance-copy">
                      <span className="ws-account-balance-label">{tr(S.accountBalance)}</span>
                      <span className="ws-account-balance-value">{accountUsageContent.value}</span>
                    </span>
                    <span className="ws-account-balance-detail">{accountUsageContent.detail}</span>
                  </span>
                ) : null}
                <span className="ws-account-chip">
                  <Avatar size="sm" name={accountLabel ?? "Guest"} />
                  <span className="ws-account-name" title={accountLabel}>
                    {accountLabel}
                  </span>
                </span>
                {onSignIn ? <Button size="sm" onClick={onSignIn}>Sign in to enable AI</Button> : <Tooltip content={tr(COMMON.logout)}>
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={<LogOut size={15} aria-hidden="true" />}
                    onClick={onLogout}
                  >
                    {tr(COMMON.logout)}
                  </Button>
                </Tooltip>}
              </div>
            ) : null}
          </div>
        </header>

        <div className="ws-body" data-active-view={activeView} data-sidebar-collapsed={(!isWriting && sidebarCollapsed) || undefined} data-outline-collapsed={(writingEnabled && outlineCollapsed) || undefined} data-companion-hidden={(isWriting && companionHidden) || undefined}>
          <nav className="ws-nav" aria-label={tr(COMMON.episodes)}>
            <div className="ws-nav-head">
              <span className="ws-nav-title">{tr(COMMON.episodes)}</span>
              {onNewEpisode ? (
                <Tooltip content={tr(COMMON.newEpisode)}>
                  <IconButton label={tr(COMMON.newEpisode)} size="sm" onClick={onNewEpisode}>
                    <Plus size={16} aria-hidden="true" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </div>
            <ScrollArea className="ws-nav-scroll" viewportClassName="ws-nav-viewport">
              {episodes.length === 0 ? (
                <p className="ws-nav-empty">{tr(COMMON.noEpisodes)}</p>
              ) : (
                <ul className="ws-nav-list">
                  {episodes.map((ep) => {
                    const active = ep.id === episodeId;
                    return (
                      <li key={ep.id}>
                        <button
                          type="button"
                          className="ws-nav-item"
                          data-active={active || undefined}
                          aria-current={active ? "true" : undefined}
                          onClick={() => onSelectEpisode(ep.id)}
                        >
                          {ep.id}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </ScrollArea>
          </nav>

          <Tabs
            className="ws-surface"
            value={activeView}
            onValueChange={(v) => onSelectView(v as WorkspaceView)}
          >
            <div className="ws-surface-bar">
              <TabsList variant={writingEnabled ? "underline" : "pill"} aria-label={tr(S.centralView)}>
                {views.map((v) => (
                  <TabsTrigger key={v.value} value={v.value} data-workspace-view={v.value}>
                    {v.value === "writing" && <PenLine size={15} aria-hidden="true" />}
                    {v.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="ws-surface-body">
              <TabsContent value="writing" className="ws-pane ws-writing-pane">{writingSlot}</TabsContent>
              <TabsContent value="studio" className="ws-pane">
                {studioSlot}
              </TabsContent>
              <TabsContent value="editor" className="ws-pane">
                {editorSlot}
              </TabsContent>
              <TabsContent value="canvas" className="ws-pane">
                {canvasSlot}
              </TabsContent>
              <TabsContent value="assets" className="ws-pane">
                {assetsSlot}
              </TabsContent>
              <TabsContent value="preview" className="ws-pane">
                {previewSlot}
              </TabsContent>
            </div>
          </Tabs>

          <aside className="ws-sidebar" aria-label={tr(isWriting ? S.companion : S.aiAssistant)} hidden={isWriting && companionHidden}>
            {isWriting && <div className="ws-companion-bar">
              <div className="ws-companion-switch" role="group" aria-label={tr(S.companion)}>
                <button type="button" aria-pressed={selectedCompanion === "preview"} onClick={() => selectCompanion("preview")}><Play size={14} aria-hidden="true" />{tr(S.preview)}</button>
                <button type="button" aria-pressed={selectedCompanion === "agent"} onClick={() => selectCompanion("agent")}><MessageSquare size={14} aria-hidden="true" />AI</button>
              </div>
              <IconButton label={tr(S.hideCompanion)} size="sm" onClick={() => setCompanionHidden(true)}><PanelRightClose size={16} /></IconButton>
            </div>}
            <div className="ws-companion-content" hidden={isWriting && selectedCompanion !== "agent"}>{sidebarSlot}</div>
            {isWriting && <div className="ws-companion-content ws-companion-preview" hidden={selectedCompanion !== "preview"}>{previewSlot}</div>}
          </aside>
        </div>
      </div>
    </TooltipProvider>
  );
}
